import { useMemo } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ErrorOutline, LockOutlined } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import {
	getCurrentMessageLoading,
	getCurrentSystemMessage,
	setCurrentMessage,
} from 'state/reducers/system-messages.reducer';
import { DetailsLoading } from 'sections/WebhookMonitoring/components/DetailsLoading';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { formatDateTime } from 'constants/global.constants';
import { SYSTEM_MESSAGE_ICON_PATHS } from './IconTilePicker';
import {
	SystemMessageAudienceLabelKey,
	SystemMessageAudienceType,
	SystemMessageIconLabelKey,
	type SystemMessageAudienceType as SystemMessageAudienceTypeValue,
	type SystemMessageIconType,
} from 'constants/system-messages.constants';
import {
	DetailsLabelValue,
	getDetailsModalFieldLabelSx,
	getDetailsModalFieldValueSx,
} from 'components/DetailsModal/DetailsLabelValue';
import { DetailsModalShell } from 'components/DetailsModal/DetailsModalShell';
import type { ISystemMessageDetail } from 'types/system-messages.types';
import { SystemMessageStatusSwitch } from './SystemMessageStatusSwitch';

interface MessageDetailDialogProps {
	open: boolean;
	onClose: () => void;
	disableReadOnly?: boolean;
	onDisabled?: () => void;
}

const buildAudienceDisplay = (msg: ISystemMessageDetail, t: (key: string) => string) => {
	const audienceLabel = t(SystemMessageAudienceLabelKey[msg.audienceType as SystemMessageAudienceTypeValue]);
	let display = audienceLabel;

	if (msg.targetCompanyName) {
		display = `${audienceLabel} – ${msg.targetCompanyName}`;
	}

	const recipientCount = msg.recipientContactIds?.length ?? 0;
	if (
		recipientCount > 0 &&
		(msg.audienceType === SystemMessageAudienceType.SPECIFIC_CONTACTS ||
			msg.audienceType === SystemMessageAudienceType.SPECIFIC_COMPANY_CONTACTS)
	) {
		display = `${display} (${recipientCount})`;
	}

	return display;
};

export const MessageDetailDialog = ({
	open,
	onClose,
	disableReadOnly = false,
	onDisabled,
}: MessageDetailDialogProps) => {
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const theme = useTheme();
	const msg = useAppSelector(getCurrentSystemMessage);
	const isDetailLoading = useAppSelector(getCurrentMessageLoading);
	const canDisable = hasPermission([permissionEnums.DISABLE_SYSTEM_MESSAGE]);

	const summaryPanelSx = useMemo(
		() => ({
			display: 'grid',
			gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
			gap: 2,
			columnGap: 2.5,
			rowGap: 2.5,
		}),
		[]
	);

	const fieldLabelSx = useMemo(() => getDetailsModalFieldLabelSx(theme), [theme]);
	const fieldValueSx = useMemo(() => getDetailsModalFieldValueSx(theme), [theme]);

	const contentBoxSx = useMemo(
		() => ({
			border: '1px solid',
			borderColor: alpha(theme.palette.root.mediumGreen, 0.5),
			borderRadius: 1,
			p: 1.5,
			backgroundColor: theme.palette.grey[100],
			maxWidth: '100%',
			overflowWrap: 'anywhere',
			wordBreak: 'break-word',
			overflowX: 'hidden',
			'& p': {
				margin: 0,
				overflowWrap: 'anywhere',
				wordBreak: 'break-word',
			},
			'& a': { color: theme.palette.primary.main },
		}),
		[theme]
	);

	const sectionPaddingSx = useMemo(
		() => ({
			pt: 2,
			pb: 3,
			px: { xs: 2, sm: 2.5 },
			width: '100%',
			boxSizing: 'border-box' as const,
		}),
		[]
	);

	const handleClose = () => {
		dispatch(setCurrentMessage(null));
		onClose();
	};

	if (!open) {
		return null;
	}

	const iconTypeLabel = msg
		? t(SystemMessageIconLabelKey[msg.iconType as SystemMessageIconType])
		: '';
	const subtitle = msg?.createdBy?.displayName
		? msg.createdAt
			? `${iconTypeLabel} • ${t('page.systemMessages.detail.createdAtPrefix')}${formatDateTime(
					msg.createdAt
			  )} - ${msg.createdBy.displayName}`
			: `${iconTypeLabel} • ${msg.createdBy.displayName}`
		: iconTypeLabel;
	const audienceDisplay = msg ? buildAudienceDisplay(msg, t) : '';
	const iconPath = msg
		? SYSTEM_MESSAGE_ICON_PATHS[msg.iconType as SystemMessageIconType]
		: undefined;
	const footerHintKey = disableReadOnly
		? 'page.systemMessages.detail.disablePermissionHint'
		: 'page.systemMessages.detail.disableOneWayHint';
	const statusReadOnly = disableReadOnly || !canDisable;

	return (
		<DetailsModalShell
			open={open}
			onClose={handleClose}
			ariaLabelledBy="system-message-detail-dialog-title"
			closeLabel={t('label.close')}
			title={t('page.systemMessages.detail.title')}
			subtitle={isDetailLoading ? '' : subtitle}
			icon={
				iconPath ? (
					<Box
						sx={{
							width: 44,
							height: 44,
							borderRadius: '50%',
							background: alpha(theme.palette.root.forestGreen, 0.12),
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
						}}
					>
						<img src={iconPath} width={22} height={22} alt="" />
					</Box>
				) : null
			}
			contentPadding="none"
			contentOverflow="auto"
			footer={
				msg ? (
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						width="100%"
						flexWrap="wrap"
						gap={1.5}
					>
						<Stack direction="row" alignItems="center" gap={1} sx={{ color: theme.palette.root.grey }}>
							{disableReadOnly ? (
								<LockOutlined sx={{ fontSize: 18 }} />
							) : (
								<ErrorOutline sx={{ fontSize: 18 }} />
							)}
							<Typography variant="body2">{t(footerHintKey)}</Typography>
						</Stack>
						<SystemMessageStatusSwitch
							messageId={msg.id}
							isLive={msg.isLive}
							readOnly={statusReadOnly}
							onDisabled={onDisabled}
						/>
					</Stack>
				) : null
			}
		>
			{isDetailLoading ? (
				<Box sx={sectionPaddingSx}>
					<DetailsLoading />
				</Box>
			) : msg ? (
				<Box sx={sectionPaddingSx}>
					<Stack spacing={2} sx={{ WebkitFontSmoothing: 'antialiased' }}>
					<Box sx={summaryPanelSx}>
						<DetailsLabelValue
							label={t('page.systemMessages.table.column.internalDescription')}
							value={msg.internalDescription}
						/>
						{msg.createdBy?.displayName ? (
							<DetailsLabelValue
								label={t('page.systemMessages.table.column.createdBy')}
								value={msg.createdBy.displayName}
							/>
						) : null}
						{msg.createdAt ? (
							<DetailsLabelValue
								label={t('page.pendingRequests.field.createdAt')}
								value={formatDateTime(msg.createdAt)}
							/>
						) : null}
						<DetailsLabelValue label={t('label.type')} value={iconTypeLabel} />
						<DetailsLabelValue
							label={t('page.systemMessages.table.column.audience')}
							value={audienceDisplay}
						/>
						<DetailsLabelValue
							label={t('page.systemMessages.table.column.startDate')}
							value={formatDateTime(msg.startDate)}
						/>
						<DetailsLabelValue
							label={t('page.systemMessages.table.column.endDate')}
							value={formatDateTime(msg.endDate)}
						/>
						<DetailsLabelValue
							label={t('page.systemMessages.table.column.confirmedCount')}
							value={msg.confirmedCount ?? 0}
						/>
					</Box>

					<Box sx={summaryPanelSx}>
						<Stack spacing={0.5}>
							<Typography component="span" sx={fieldLabelSx}>
								{t('page.systemMessages.detail.titleHe')}
							</Typography>
							<Typography component="span" sx={fieldValueSx}>
								{msg.titleHe}
							</Typography>
						</Stack>
						<Stack spacing={0.5}>
							<Typography component="span" sx={fieldLabelSx}>
								{t('page.systemMessages.detail.titleEn')}
							</Typography>
							<Typography component="span" sx={fieldValueSx}>
								<Box component="span" dir="ltr" sx={{ unicodeBidi: 'isolate', display: 'inline' }}>
									{msg.titleEn}
								</Box>
							</Typography>
						</Stack>
					</Box>

					<Stack spacing={0.5}>
						<Typography component="span" sx={fieldLabelSx}>
							{t('page.systemMessages.detail.contentHe')}
						</Typography>
						<Box sx={contentBoxSx} dangerouslySetInnerHTML={{ __html: msg.contentHe ?? '' }} />
					</Stack>

					<Stack spacing={0.5}>
						<Typography component="span" sx={fieldLabelSx}>
							{t('page.systemMessages.detail.contentEn')}
						</Typography>
						<Box sx={contentBoxSx} dir="ltr" dangerouslySetInnerHTML={{ __html: msg.contentEn ?? '' }} />
					</Stack>
				</Stack>
			</Box>
			) : null}
		</DetailsModalShell>
	);
};
